/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {PropsWithChildren, useCallback} from 'react';
import {allExpanded, defaultStyles, JsonView} from 'react-json-view-lite';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {copyToClipBoard} from '@ion/core/src/utils/BrowserUtils';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {CopyIcon} from '@ion/dls/src/icons/CopyIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {Color, IconSize} from '@ion/dls/src/types/types';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {isMFGInfoArray} from './ViewDeviceEditorUtils';
import 'react-json-view-lite/dist/index.css';

export interface SplitInformationViewProps {
  text?: string | GenericObject;
  isCalampSup?: boolean;
}
const DISTANCE_FROM_TOP = 200;
const DISTANCE_FROM_BOTTOM = 93;

const useStyles = makeStyles(() => ({
  flexContainer : {
    display   : 'flex',
    gap       : '15px',
    height    : `calc(100vh - ${DISTANCE_FROM_BOTTOM + DISTANCE_FROM_TOP}px)`,
    marginTop : '20px'
  },
  side : {
    backgroundColor : dlsColor('white'),
    overflow        : 'scroll',
    padding         : '16px 10px'
  },
  left : {
    flex : 1
  },
  right : {
    flex : 2
  },
  text : {
    whiteSpace : 'pre-wrap'
  },
  copybtn : {
    margin    : '-25px 4px',
    position  : 'sticky',
    textAlign : 'right',
    top       : -2,
    zIndex    : 1
  },
  copyAction : {
    backgroundColor : dlsColor('white'),
    border          : 'none',
    cursor          : 'pointer',
    padding         : '8px 12px 6px 11px',
    borderRadius    : '4px',
    '&:hover'       : {
      backgroundColor : dlsColor('paleBlue'),
      '& svg'         : {
        '& path' : {
          fill : dlsColor('aaBlue')
        }
      }
    }
  }
}));

/**
 * Displays a split-view information viewer where any children are displayed in the left side
 * and the given `text` is displayed on the right side.
 */
export const SplitInformationView = ({
  children,
  isCalampSup = false,
  text = translate('admin-devices:view.manufacturing.noDataAvailable')
}: PropsWithChildren<SplitInformationViewProps>) => {
  const classes  = useStyles();
  const dispatch = useDispatch();

  const copyValuesToClipboard = useCallback(() => {
    copyToClipBoard(safeJSONStringify(text) as any);
    dispatch(toggleUserPrompt({
      message          : translate('common:clipboard.copySuccess'),
      autoHideDuration : 1000
    }));
  }, [text]);

  return (
    <div className={classes.flexContainer}>
      <div className={classnames(classes.side, classes.left)}>
        {children}
      </div>
      <div className={classnames(classes.side, classes.right)}>
        <>
          <Render.If condition={isCalampSup}>
            <div className={classes.copybtn}>
              <button className={classes.copyAction} onClick={copyValuesToClipboard}
                disabled={isObjectEmpty(text as GenericObject) || isMFGInfoArray(text as GenericObject)}>
                <CopyIcon size={IconSize.medium} color={Color.grey3}/>
              </button>
            </div>
          </Render.If>
          <p className={classes.text}>
            <Render>
              <Render.If condition={!isCalampSup || isMFGInfoArray(text as GenericObject)}>{text as string || ''}</Render.If>
              <Render.Else>
                <Render>
                  <Render.If condition={!isObjectEmpty(text as GenericObject)}>
                    <form style={{height: '100%', paddingTop: 10}}>
                      <pre><JsonView data={text || ''} shouldExpandNode={allExpanded} style={defaultStyles} /></pre>
                    </form>
                  </Render.If>
                  <Render.Else>
                    {translate('admin-devices:view.manufacturing.noDataAvailable')}
                  </Render.Else>
                </Render>
              </Render.Else>
            </Render>
          </p>
        </>
      </div>
    </div>
  );
};

export default SplitInformationView;
